/**
 * Client workspace · Services tab — Task 046.
 *
 * Shows the current package assignment for this client (if any),
 * including the included services and the capability keys those
 * services unlock. Managers with `billing.subscription.manage` can
 * assign a new package; client roles read-only.
 *
 * Task 151-C adds the explicit status-transition surface: an active
 * package can be replaced (assign a draft → atomically end the old
 * active + activate the new) or ended; drafts can be activated or
 * cancelled. All transitions go through `update-client-package-assignment`
 * behind an explicit confirm; assignment stays one-active-per-client.
 *
 * Tab/page guard: this tab is registered in `ClientWorkspaceLayout`
 * with a permission check (`contract.read` OR
 * `billing.subscription.manage`); both agency-side managers and
 * client_admin can land here.
 *
 * Follow-up note (per Task 046 spec): client workspace tabs should
 * eventually be gated by BOTH user permission AND active client
 * package capability. Today they're gated by user permission only.
 * The `capability_keys` array on the assignment row gives the wire
 * data the future gate would consume.
 */

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { isApiError, KmiApiError } from '../../api/client';
import {
  assignClientPackage,
  listAgencyPackages,
  listClientPackageAssignments,
  recordPackageServiceDelivery,
  updateClientPackageAssignment,
} from '../../api/endpoints';
import type {
  AgencyPackageRow,
  AssignmentCommercialTermsSnapshot,
  AssignmentServiceSnapshot,
  ClientPackageAssignmentRow,
  ClientPackageAssignmentStatus,
  PackageServiceDeliverable,
  PricingModel,
  ServiceCadence,
  SystemCapabilityKey,
} from '../../api/types';
import { useMeContext } from '../../context/MeContextProvider';
import { capabilityLabelAr, hasAnyPermission, isClientOnly, PERM } from '../../permissions';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import type { ClientWorkspaceOutletContext } from '../ClientWorkspaceLayout';
import { PackageProgressSection } from './PackageProgressSection';
import {
  buildRecordDeliveryInput,
  recordableServices,
  todayDateInputValue,
  type RecordDeliveryFormState,
} from './recordDelivery';

type LoadState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly assignments: ReadonlyArray<ClientPackageAssignmentRow>;
      readonly packages: ReadonlyArray<AgencyPackageRow>;
    }
  | { readonly status: 'error'; readonly error: unknown };

/** Task 151-C: a transition awaiting an explicit confirm. */
interface ReplaceForm {
  readonly packageId: string;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly notes: string | null;
}
type PendingTransition =
  | { readonly kind: 'end'; readonly assignmentId: string; readonly message: string }
  | { readonly kind: 'cancel'; readonly assignmentId: string; readonly message: string }
  | { readonly kind: 'activate'; readonly assignmentId: string; readonly message: string }
  | { readonly kind: 'replace'; readonly form: ReplaceForm; readonly message: string };

export function ClientServicesPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const workspace = useOutletContext<ClientWorkspaceOutletContext>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  // Task 151-C — explicit transition state (replace / activate / end /
  // cancel). `pending` holds a transition awaiting confirmation.
  const [pending, setPending] = useState<PendingTransition | null>(null);
  const [mutating, setMutating] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  // Task 151-D.2 — bumped after a delivery is recorded so PackageProgressSection refetches.
  const [deliveryRefreshKey, setDeliveryRefreshKey] = useState(0);

  const canManage =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.BILLING_SUBSCRIPTION_MANAGE);
  const clientOnly = me ? isClientOnly(me) : false;

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    Promise.all([
      listClientPackageAssignments({ clientId, signal: ctrl.signal }),
      // Active packages list — only managers actually use it (to assign).
      // Client-side users get empty array if RLS blocks; we tolerate that.
      canManage
        ? listAgencyPackages({ active: true, signal: ctrl.signal })
        : Promise.resolve({ packages: [] as ReadonlyArray<AgencyPackageRow> }),
    ])
      .then(([a, p]) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', assignments: a.assignments, packages: p.packages });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey, canManage]);

  if (!clientId) return null;
  if (state.status === 'loading') return <LoadingState title="جاري تحميل خدمات العميل…" />;
  if (state.status === 'error')
    return (
      <ErrorState
        title="تعذر تحميل خدمات العميل"
        error={state.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );

  const refresh = () => setReloadKey((n) => n + 1);
  const cid = clientId;

  const active = state.assignments.find((a) => a.status === 'active') ?? null;
  const drafts = state.assignments.filter((a) => a.status === 'draft');
  const archive = state.assignments.filter(
    (a) => a.status === 'paused' || a.status === 'cancelled' || a.status === 'ended',
  );

  // Execute the confirmed transition. Replacement assigns a DRAFT first
  // (the old active stays active until the atomic activate succeeds); if
  // the activate step fails, the draft is left recoverable with a clear
  // message rather than ending the old active.
  async function runPending() {
    if (!pending) return;
    setMutating(true);
    setMutationError(null);
    try {
      if (pending.kind === 'replace') {
        let createdId: string;
        try {
          const created = await assignClientPackage({
            input: {
              client_id: cid,
              package_id: pending.form.packageId,
              status: 'draft',
              starts_at: pending.form.startsAt,
              ends_at: pending.form.endsAt,
              notes: pending.form.notes,
            },
          });
          createdId = created.assignment.id;
        } catch (assignErr) {
          setMutating(false);
          setPending(null);
          handleApiErr(assignErr, setMutationError);
          return;
        }
        try {
          await updateClientPackageAssignment({
            input: { assignment_id: createdId, action: 'activate' },
          });
        } catch {
          setMutating(false);
          setPending(null);
          setMutationError(
            'تم إنشاء الباقة الجديدة كمسودّة لكن تعذّر تفعيلها. الباقة الحالية ما زالت فعّالة — يمكنك تفعيل المسودّة من قسم «مسودّات» بالأسفل.',
          );
          refresh();
          return;
        }
      } else {
        await updateClientPackageAssignment({
          input: { assignment_id: pending.assignmentId, action: pending.kind },
        });
      }
      setMutating(false);
      setPending(null);
      workspace.refreshPackageAssignments();
      refresh();
    } catch (err) {
      setMutating(false);
      setPending(null);
      handleApiErr(err, setMutationError);
    }
  }

  // No active package → assign directly as active (not a replacement, so
  // no confirm). With an active package, the form routes through
  // `onRequestReplace` → explicit confirm instead.
  async function assignActive(form: ReplaceForm) {
    setMutating(true);
    setMutationError(null);
    try {
      await assignClientPackage({
        input: {
          client_id: cid,
          package_id: form.packageId,
          status: 'active',
          starts_at: form.startsAt,
          ends_at: form.endsAt,
          notes: form.notes,
        },
      });
      setMutating(false);
      workspace.refreshPackageAssignments();
      refresh();
    } catch (err) {
      setMutating(false);
      handleApiErr(err, setMutationError);
    }
  }

  return (
    <div className="stack-md">
      <ClientFoundationCard />

      {pending ? (
        <div
          className="banner banner-info"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>{pending.message}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="auth-cta"
              onClick={runPending}
              disabled={mutating}
              style={{ width: 'auto' }}
            >
              {mutating ? <span className="spinner" /> : null}
              <span>تأكيد</span>
            </button>
            <button
              type="button"
              className="btn-text"
              onClick={() => setPending(null)}
              disabled={mutating}
              style={{ padding: '10px 14px' }}
            >
              إلغاء
            </button>
          </div>
        </div>
      ) : null}

      {mutationError ? (
        <div className="banner banner-error" role="alert">
          {mutationError}
        </div>
      ) : null}

      <ActiveAssignmentCard
        assignment={active}
        canManage={canManage}
        onEnd={
          active
            ? () =>
                setPending({
                  kind: 'end',
                  assignmentId: active.id,
                  message: `سيتم إنهاء الباقة الحالية «${active.package_name ?? 'باقة'}». ستنتقل إلى السجلّ السابق (لا تُحذف).`,
                })
            : undefined
        }
        onRefreshDefinition={
          // Task 280H — re-snapshot the SAME package with its latest definition.
          // Reuses the `replace` transition (draft → atomic activate, recoverable
          // on partial failure); preserves the active row's starts_at/ends_at
          // (already valid for `ends_at >= starts_at`) and notes.
          active && active.package_id
            ? () =>
                setPending({
                  kind: 'replace',
                  form: {
                    packageId: active.package_id,
                    startsAt: active.starts_at,
                    endsAt: active.ends_at,
                    notes: active.notes,
                  },
                  message:
                    'سيتم إنشاء نسخة جديدة من نفس الباقة باستخدام أحدث تعريف لها، ثم استبدال الباقة الحالية بها. السجل السابق سيبقى محفوظاً.',
                })
            : undefined
        }
      />

      {active ? (
        <PackageProgressSection clientId={clientId} reloadSignal={deliveryRefreshKey} />
      ) : null}

      {canManage && active ? (
        <RecordDeliverySection
          assignment={active}
          onRecorded={() => setDeliveryRefreshKey((n) => n + 1)}
        />
      ) : null}

      {clientOnly && active ? (
        <ClientCapabilityCardsSection
          capabilities={resolveActiveCapabilityKeys(active)}
          clientId={cid}
        />
      ) : null}

      {drafts.length > 0 ? (
        <section className="card" style={{ padding: 22 }}>
          <div className="sec-title">
            <h2>مسودّات</h2>
            <span className="meta">{drafts.length}</span>
          </div>
          <AssignmentList
            assignments={drafts}
            renderActions={
              canManage
                ? (a) => (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn-text"
                        disabled={mutating}
                        onClick={() =>
                          setPending({
                            kind: 'activate',
                            assignmentId: a.id,
                            message: active
                              ? `سيتم إنهاء الباقة الحالية «${active.package_name ?? 'باقة'}» وتفعيل «${a.package_name ?? 'باقة'}».`
                              : `سيتم تفعيل «${a.package_name ?? 'باقة'}» كباقة حالية.`,
                          })
                        }
                      >
                        تفعيل
                      </button>
                      <button
                        type="button"
                        className="btn-text"
                        disabled={mutating}
                        style={{ color: 'var(--rose)' }}
                        onClick={() =>
                          setPending({
                            kind: 'cancel',
                            assignmentId: a.id,
                            message: `سيتم إلغاء المسودّة «${a.package_name ?? 'باقة'}» (تبقى في السجلّ، لا تُحذف).`,
                          })
                        }
                      >
                        إلغاء المسودّة
                      </button>
                    </div>
                  )
                : undefined
            }
          />
        </section>
      ) : null}

      {canManage ? (
        <AssignPackageSection
          packages={state.packages}
          hasActive={active !== null}
          submitting={mutating}
          onAssignActive={assignActive}
          onRequestReplace={(form, newName) =>
            setPending({
              kind: 'replace',
              form,
              message: `سيتم إنهاء الباقة الحالية «${active?.package_name ?? 'باقة'}» وتفعيل «${newName}». الباقة الحالية تبقى فعّالة حتى ينجح التفعيل.`,
            })
          }
        />
      ) : null}

      {archive.length > 0 ? (
        <section className="card" style={{ padding: 22 }}>
          <div className="sec-title">
            <h2>سجلّ سابق</h2>
            <span className="meta">{archive.length}</span>
          </div>
          <AssignmentList assignments={archive} />
        </section>
      ) : null}

      {state.assignments.length === 0 && !canManage ? (
        <EmptyState
          title="لا توجد باقة معيّنة لهذا العميل"
          description="الإدارة ستقوم بتعيين باقة في الوقت المناسب."
        />
      ) : null}
    </div>
  );
}

/**
 * Capabilities that a client-tier actor can currently navigate to inside
 * their own workspace and see real data for. Reports is the only one
 * because `report_jobs_select` (see
 * supabase/migrations/20260423180000_reports_exports_foundation.sql L711-735)
 * has both an agency branch AND a client branch — the other capability
 * tables are gated by `app.current_user_has_agency_role()` and would
 * return empty rows even if we granted the read perm. Until that RLS is
 * intentionally extended in a separate task, the cards label non-reports
 * capabilities as "managed by the agency, surfaced in reports".
 */
const CLIENT_VISIBLE_CAPABILITY_KEYS: ReadonlyArray<string> = ['reports'];

function resolveActiveCapabilityKeys(
  assignment: ClientPackageAssignmentRow,
): ReadonlyArray<string> {
  // Mirror ActiveAssignmentCard's snapshot-vs-live discriminator so the
  // capability cards quote the same source of truth as the snapshot
  // section above them.
  const termsSnapshot = assignment.commercial_terms_snapshot as
    | AssignmentCommercialTermsSnapshot
    | Readonly<Record<string, unknown>>;
  const hasTask047Snapshot =
    typeof termsSnapshot?.snapshot_taken_at === 'string' &&
    (termsSnapshot.snapshot_taken_at as string).length > 0;
  return hasTask047Snapshot ? assignment.capability_keys_snapshot : assignment.capability_keys;
}

function capabilityClientDescription(key: string): string {
  switch (key) {
    case 'paid_media':
      return 'الوكالة تدير حسابات الإعلانات والحملات على Meta وGoogle وTikTok وSnapchat نيابةً عنك.';
    case 'creative':
      return 'الوكالة تنتج وتنظّم الأصول الإبداعية المرتبطة بحملاتك.';
    case 'content':
      return 'الوكالة تخطّط وتنتج المحتوى الرقمي وفق الخطة المعتمدة.';
    case 'publishing':
      return 'الوكالة تتولى نشر المحتوى على قنواتك الرقمية وفق الجدولة.';
    case 'strategy':
      return 'الوكالة تبني وتطوّر الاستراتيجية الأساسية لعلامتك.';
    case 'reports':
      return 'تقارير الأداء الدورية تُجمع وتُسلّم لك مباشرةً في تاب التقارير.';
    case 'influencers':
      return 'الوكالة تدير حملات المؤثرين وملاحظاتها التشغيلية.';
    case 'crm':
      return 'الوكالة تدير العملاء المحتملين والصفقات ضمن CRM الداخلي.';
    case 'calendar':
      return 'الوكالة تدير التقويم التحريري والإعلاني.';
    case 'client_onboarding':
      return 'مرحلة تأهيل العميل تتم عبر خطوات منظّمة قبل التشغيل.';
    case 'custom':
      return 'خدمة مخصّصة وفق ما تم الاتفاق عليه مع الوكالة.';
    default:
      return 'تفاصيل هذه القدرة ضمن نطاق الباقة.';
  }
}

function ClientCapabilityCardsSection({
  capabilities,
  clientId,
}: {
  readonly capabilities: ReadonlyArray<string>;
  readonly clientId: string;
}) {
  if (capabilities.length === 0) return null;
  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>قدراتك المفعّلة في هذه الباقة</h2>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 14 }}>
        هذه الخدمات الفنية المضمّنة في باقتك. ما تديره الوكالة يصلك في تاب التقارير، وما هو متاح
        لعرضك المباشر تفتحه من تابه المخصّص.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))',
          gap: 12,
        }}
      >
        {capabilities.map((c) => {
          const visible = CLIENT_VISIBLE_CAPABILITY_KEYS.includes(c);
          return (
            <article
              key={c}
              style={{
                padding: 14,
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
                background: visible ? 'var(--paper)' : 'var(--cream-2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <strong style={{ fontSize: 14 }}>
                  {capabilityLabelAr(c as SystemCapabilityKey)}
                </strong>
                <span
                  className={visible ? 'pill pill-active' : 'pill pill-info'}
                  style={{ fontSize: 10 }}
                >
                  {visible ? 'متاحة لك' : 'تديرها الوكالة'}
                </span>
              </div>
              <p className="tiny" style={{ color: 'var(--ink-2)', margin: 0 }}>
                {capabilityClientDescription(c)}
              </p>
              {visible ? (
                <Link
                  to={`/app/clients/${clientId}/reports`}
                  className="btn-text"
                  style={{ fontSize: 12, alignSelf: 'flex-start' }}
                >
                  افتح التقارير ←
                </Link>
              ) : (
                <span className="tiny" style={{ color: 'var(--muted)' }}>
                  تظهر نتائجها في تاب التقارير
                </span>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ClientFoundationCard() {
  const items = [
    {
      title: 'التأهيل',
      body: 'خطوة أساسية لجمع بيانات العميل قبل التفعيل.',
    },
    {
      title: 'الاستراتيجية',
      body: 'اتجاه العمل الذي تُبنى عليه خطة المحتوى والحملات.',
    },
    {
      title: 'التقارير الأساسية',
      body: 'متابعة وقياس مرتبطة بالخدمات المفعّلة لهذا العميل.',
    },
  ] as const;

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>أساسيات مساحة العميل</h2>
        <span className="pill pill-active">مفعّلة افتراضياً</span>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 12 }}>
        هذه ليست جزءاً من الباقة التجارية. كل عميل يمر بها حتى يكون العمل داخل المنصة منظماً وقابلاً
        للقياس.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
          gap: 10,
        }}
      >
        {items.map((item) => (
          <div
            key={item.title}
            style={{
              padding: '12px',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-md)',
              background: 'var(--cream-2)',
            }}
          >
            <strong style={{ display: 'block', marginBottom: 4 }}>{item.title}</strong>
            <p className="tiny" style={{ color: 'var(--ink-2)', margin: 0 }}>
              {item.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ActiveAssignmentCard({
  assignment,
  canManage,
  onEnd,
  onRefreshDefinition,
}: {
  readonly assignment: ClientPackageAssignmentRow | null;
  readonly canManage: boolean;
  /** "End current package" — manager only; present only when active. */
  readonly onEnd?: () => void;
  /**
   * Task 280H — "Update to the current package definition": re-snapshot the
   * SAME package with its latest definition (draft → atomic activate). Manager
   * only; present only when active with a `package_id`.
   */
  readonly onRefreshDefinition?: () => void;
}) {
  if (!assignment) {
    return (
      <section className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الباقة الحالية</h2>
        </div>
        <EmptyState
          title="لا توجد باقة فعّالة"
          description={
            canManage
              ? 'استخدم القسم بالأسفل لتعيين باقة لهذا العميل.'
              : 'لم يتم تعيين باقة فعّالة لهذا العميل بعد.'
          }
        />
      </section>
    );
  }

  // Task 047: prefer the snapshot for contractual display. The
  // discriminator for "this is a Task 047 assignment with a real
  // snapshot" is `commercial_terms_snapshot.snapshot_taken_at` — that
  // field only exists on assignments created post-Task-047.
  // Empty-array snapshots (legacy Task 046 assignments) get the
  // live-package fallback; legitimate Task 047 assignments with
  // genuinely-zero included services get the snapshot view (Codex P8).
  const termsSnapshot = assignment.commercial_terms_snapshot as
    | AssignmentCommercialTermsSnapshot
    | Readonly<Record<string, unknown>>;
  const hasTask047Snapshot =
    typeof termsSnapshot?.snapshot_taken_at === 'string' &&
    (termsSnapshot.snapshot_taken_at as string).length > 0;
  const snapshotServices = hasTask047Snapshot
    ? (assignment.services_snapshot as ReadonlyArray<AssignmentServiceSnapshot>)
    : null;
  const snapshotCapabilityKeys = hasTask047Snapshot
    ? assignment.capability_keys_snapshot
    : assignment.capability_keys;
  const packageNameDisplay = hasTask047Snapshot
    ? typeof termsSnapshot.package_name === 'string'
      ? (termsSnapshot.package_name as string)
      : (assignment.package_name ?? 'باقة بدون اسم')
    : (assignment.package_name ?? 'باقة بدون اسم');
  const packageDescriptionDisplay = hasTask047Snapshot
    ? typeof termsSnapshot.package_description === 'string'
      ? (termsSnapshot.package_description as string)
      : assignment.package_description
    : assignment.package_description;

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>{packageNameDisplay}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Task 280F (Phase 1C) — make it visible that the active package is a
              snapshot frozen at assignment time (only true for Task 047
              snapshots; legacy assignments render live data). */}
          {hasTask047Snapshot ? (
            <span className="pill pill-info" style={{ fontSize: 11 }}>
              مثبّتة عند التعيين
            </span>
          ) : null}
          <span className={`pill pill-${statusPill(assignment.status)}`}>
            {statusLabel(assignment.status)}
          </span>
        </div>
      </div>
      {canManage && (onRefreshDefinition || onEnd) ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {onRefreshDefinition && assignment.package_id ? (
            <button
              type="button"
              className="btn-text"
              onClick={onRefreshDefinition}
              style={{ color: 'var(--tangerine)', padding: '6px 12px' }}
            >
              تحديث إلى تعريف الباقة الحالي
            </button>
          ) : null}
          {onEnd ? (
            <button
              type="button"
              className="btn-text"
              onClick={onEnd}
              style={{ color: 'var(--rose)', padding: '6px 12px' }}
            >
              إنهاء الباقة الحالية
            </button>
          ) : null}
        </div>
      ) : null}
      {packageDescriptionDisplay ? (
        <p className="body" style={{ color: 'var(--ink-2)', marginBottom: 14 }}>
          {packageDescriptionDisplay}
        </p>
      ) : null}
      {snapshotServices ? (
        <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 12 }}>
          هذه شروط الباقة كما ثُبّتت عند التعيين. تعديل تعريف الباقة لاحقًا لا يحدّث الباقة المفعّلة
          تلقائيًا.
        </p>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
          gap: 14,
          marginBottom: 14,
        }}
      >
        <Field
          label="السعر"
          value={
            assignment.price_amount_snapshot !== null
              ? `${assignment.price_amount_snapshot} ${assignment.price_currency_snapshot}`
              : null
          }
        />
        <Field
          label="فترة الفوترة"
          value={billingIntervalLabel(assignment.billing_interval_snapshot)}
        />
        <Field label="تاريخ البدء" value={assignment.starts_at} />
        <Field label="تاريخ الانتهاء" value={assignment.ends_at} />
      </div>

      <div className="lbl" style={{ marginBottom: 6 }}>
        الخدمات المضمّنة ({(snapshotServices ?? assignment.services).length})
      </div>
      {snapshotServices !== null ? (
        snapshotServices.length === 0 ? (
          <p className="tiny" style={{ color: 'var(--muted)' }}>
            لا توجد خدمات مضمّنة في هذه الباقة.
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {snapshotServices
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((s) => (
                <SnapshotServiceCard key={s.service_id} service={s} />
              ))}
          </ul>
        )
      ) : assignment.services.length === 0 ? (
        <p className="tiny" style={{ color: 'var(--muted)' }}>
          لا توجد خدمات مضمّنة في هذه الباقة.
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {assignment.services.map((s) => (
            <li
              key={s.service_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-sm)',
                gap: 10,
              }}
            >
              <div>
                <strong style={{ fontSize: 13 }}>{s.service_name}</strong>
                {s.notes ? (
                  <div className="tiny" style={{ color: 'var(--muted)', marginTop: 4 }}>
                    {s.notes}
                  </div>
                ) : null}
              </div>
              <span className="pill pill-info">{capabilityLabelAr(s.system_capability_key)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="lbl" style={{ marginTop: 14, marginBottom: 6 }}>
        القدرات المفعّلة
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {snapshotCapabilityKeys.length === 0 ? (
          <span className="tiny" style={{ color: 'var(--muted)' }}>
            لا توجد قدرات مفعّلة
          </span>
        ) : (
          snapshotCapabilityKeys.map((c) => (
            <span key={c} className="pill pill-active">
              {capabilityLabelAr(c)}
            </span>
          ))
        )}
      </div>

      {assignment.notes ? (
        <p className="body" style={{ marginTop: 14, color: 'var(--ink-2)' }}>
          {assignment.notes}
        </p>
      ) : null}
    </section>
  );
}

function SnapshotServiceCard({ service }: { readonly service: AssignmentServiceSnapshot }) {
  const pricingLabel = pricingModelLabel(service.pricing_model);
  const cadenceLabel = serviceCadenceLabel(service.cadence);
  const priceFragments: string[] = [];
  if (service.retainer_amount !== null && service.retainer_amount !== undefined) {
    priceFragments.push(`${service.retainer_amount} ${service.retainer_currency}`);
  }
  if (service.spend_percentage !== null && service.spend_percentage !== undefined) {
    priceFragments.push(`${service.spend_percentage}% من الإنفاق`);
  }
  return (
    <li
      style={{
        padding: '12px',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        opacity: service.included ? 1 : 0.7,
        background: service.included ? 'var(--paper)' : 'var(--cream-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>{service.service_name}</strong>
            {!service.included ? (
              <span className="pill pill-archived" style={{ fontSize: 10 }}>
                غير مفعّلة
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
            {service.capability_keys.map((c) => (
              <span
                key={c}
                className={
                  c === service.primary_capability_key ? 'pill pill-active' : 'pill pill-info'
                }
                style={{ fontSize: 10 }}
              >
                {capabilityLabelAr(c as SystemCapabilityKey)}
                {c === service.primary_capability_key ? ' ★' : ''}
              </span>
            ))}
          </div>
        </div>
        <div className="tiny" style={{ color: 'var(--muted)', textAlign: 'end' }}>
          {service.quantity !== null && service.quantity !== undefined ? (
            <div>الكمية: {String(service.quantity)}</div>
          ) : null}
          {cadenceLabel !== '—' ? <div>الوتيرة: {cadenceLabel}</div> : null}
          {pricingLabel !== '—' ? <div>التسعير: {pricingLabel}</div> : null}
          {priceFragments.length > 0 ? <div>{priceFragments.join(' + ')}</div> : null}
        </div>
      </div>
      {service.deliverables.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <div className="lbl" style={{ fontSize: 11, marginBottom: 4 }}>
            التسليمات
          </div>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {service.deliverables.map((d, i) => (
              <li key={i} className="tiny" style={{ color: 'var(--ink-2)' }}>
                {deliverableTypeLabel(d.type)}: {d.label}
                {d.quantity !== null && d.quantity !== undefined ? ` · ${d.quantity}` : ''}
                {d.cadence ? ` · ${serviceCadenceLabel(d.cadence)}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {service.terms_notes ? (
        <p
          className="body"
          style={{ marginTop: 8, color: 'var(--ink-2)', fontSize: 12, whiteSpace: 'pre-wrap' }}
        >
          {service.terms_notes}
        </p>
      ) : null}
    </li>
  );
}

/**
 * Task 151-E — manager-only MANUAL ADJUSTMENT / correction for the active
 * package. This is NOT the primary progress mechanism: progress is recorded
 * automatically from real domain events (e.g. a published post via the
 * publish_jobs trigger). This form remains only as an admin fallback for
 * corrections. Picks an included snapshot service, quantity, delivered date
 * (default today) and an optional note, posts via
 * `recordPackageServiceDelivery`, then calls `onRecorded` so the parent
 * refetches `PackageProgressSection` (consumed updates).
 */
function RecordDeliverySection({
  assignment,
  onRecorded,
}: {
  readonly assignment: ClientPackageAssignmentRow;
  readonly onRecorded: () => void;
}) {
  const services = useMemo(
    () =>
      recordableServices(
        assignment.services_snapshot as ReadonlyArray<AssignmentServiceSnapshot> | null,
      ),
    [assignment],
  );
  const [form, setForm] = useState<RecordDeliveryFormState>(() => ({
    serviceId: services[0]?.service_id ?? '',
    quantity: '1',
    deliveredAt: todayDateInputValue(),
    notes: '',
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Keep the selected service valid as the snapshot changes.
  useEffect(() => {
    if (!form.serviceId || !services.some((s) => s.service_id === form.serviceId)) {
      setForm((f) => ({ ...f, serviceId: services[0]?.service_id ?? '' }));
    }
  }, [services, form.serviceId]);

  if (services.length === 0) return null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    const built = buildRecordDeliveryInput(assignment.id, form);
    if (!built.ok) {
      setError(built.error);
      return;
    }
    setSubmitting(true);
    try {
      await recordPackageServiceDelivery({ input: built.input });
      setSubmitting(false);
      setOkMsg('تم تسجيل التنفيذ وتحديث التقدّم.');
      setForm((f) => ({ ...f, quantity: '1', notes: '' }));
      onRecorded();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError);
    }
  }

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>تعديل يدوي / تصحيح</h2>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 12 }}>
        يُحتسب التقدّم تلقائياً عند نشر/إكمال العمل في المنصّة. استخدم هذا النموذج للتصحيح أو
        التعديل اليدوي فقط عند الحاجة.
      </p>
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}
        {okMsg ? (
          <div className="banner banner-info" style={{ marginBottom: 12 }}>
            {okMsg}
          </div>
        ) : null}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
            gap: 14,
          }}
        >
          <div className="auth-field" style={{ marginBottom: 0 }}>
            <label className="auth-field-label">الخدمة *</label>
            <select
              className="auth-input"
              value={form.serviceId}
              onChange={(e) => setForm((f) => ({ ...f, serviceId: e.currentTarget.value }))}
              disabled={submitting}
              style={{ paddingInlineStart: 18 }}
            >
              {services.map((s) => (
                <option key={s.service_id} value={s.service_id}>
                  {s.service_name}
                </option>
              ))}
            </select>
          </div>
          <div className="auth-field" style={{ marginBottom: 0 }}>
            <label className="auth-field-label">الكمية *</label>
            <input
              className="auth-input"
              type="number"
              min="0"
              step="any"
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.currentTarget.value }))}
              disabled={submitting}
              dir="ltr"
              style={{ paddingInlineStart: 18 }}
            />
          </div>
          <div className="auth-field" style={{ marginBottom: 0 }}>
            <label className="auth-field-label">تاريخ التنفيذ</label>
            <input
              className="auth-input"
              type="date"
              value={form.deliveredAt}
              onChange={(e) => setForm((f) => ({ ...f, deliveredAt: e.currentTarget.value }))}
              disabled={submitting}
              dir="ltr"
              style={{ paddingInlineStart: 18 }}
            />
          </div>
        </div>
        <div className="auth-field" style={{ marginTop: 14, marginBottom: 0 }}>
          <label className="auth-field-label">ملاحظة (اختياري)</label>
          <textarea
            className="auth-input"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.currentTarget.value }))}
            rows={2}
            maxLength={1000}
            disabled={submitting}
            style={{ paddingInlineStart: 18, resize: 'vertical' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button
            type="submit"
            className="auth-cta"
            disabled={submitting}
            style={{ width: 'auto' }}
          >
            {submitting ? <span className="spinner" /> : null}
            <span>تسجيل التنفيذ</span>
          </button>
        </div>
      </form>
    </section>
  );
}

function pricingModelLabel(m: PricingModel | null): string {
  switch (m) {
    case 'included':
      return 'مضمّن';
    case 'fixed':
      return 'ثابت';
    case 'percentage_of_spend':
      return 'نسبة من الإنفاق';
    case 'fixed_plus_percentage':
      return 'ثابت + نسبة';
    case 'per_campaign':
      return 'لكل حملة';
    case 'custom':
      return 'مخصّص';
    default:
      return '—';
  }
}

function serviceCadenceLabel(c: ServiceCadence | null): string {
  switch (c) {
    case 'one_time':
      return 'مرّة واحدة';
    case 'weekly':
      return 'أسبوعي';
    case 'monthly':
      return 'شهري';
    case 'quarterly':
      return 'ربع سنوي';
    case 'per_campaign':
      return 'لكل حملة';
    case 'custom':
      return 'مخصّص';
    default:
      return '—';
  }
}

function deliverableTypeLabel(t: PackageServiceDeliverable['type']): string {
  switch (t) {
    case 'design':
      return 'تصميم';
    case 'post':
      return 'منشور';
    case 'reel':
      return 'ريل';
    case 'story':
      return 'ستوري';
    case 'video':
      return 'فيديو';
    case 'campaign':
      return 'حملة';
    case 'report':
      return 'تقرير';
    case 'meeting':
      return 'اجتماع';
    case 'custom':
      return 'مخصّص';
    default:
      return t;
  }
}

function AssignmentList({
  assignments,
  renderActions,
}: {
  readonly assignments: ReadonlyArray<ClientPackageAssignmentRow>;
  /** Optional per-row actions (e.g. activate / cancel for drafts). */
  readonly renderActions?: (a: ClientPackageAssignmentRow) => ReactNode;
}) {
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {assignments.map((a) => (
        <li
          key={a.id}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <div
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
          >
            <strong>{a.package_name ?? 'باقة'}</strong>
            <span className={`pill pill-${statusPill(a.status)}`}>{statusLabel(a.status)}</span>
          </div>
          <div className="tiny" style={{ marginTop: 4, color: 'var(--muted)' }}>
            {a.starts_at ? `من ${a.starts_at}` : 'بدون تاريخ بدء'}
            {a.ends_at ? ` · إلى ${a.ends_at}` : ''}
            {' · '}
            {a.price_amount_snapshot !== null
              ? `${a.price_amount_snapshot} ${a.price_currency_snapshot}`
              : 'بدون سعر'}
          </div>
          {renderActions ? <div style={{ marginTop: 8 }}>{renderActions(a)}</div> : null}
        </li>
      ))}
    </ul>
  );
}

function AssignPackageSection({
  packages,
  hasActive,
  submitting,
  onAssignActive,
  onRequestReplace,
}: {
  readonly packages: ReadonlyArray<AgencyPackageRow>;
  readonly hasActive: boolean;
  readonly submitting: boolean;
  /** No active package → assign directly as active. */
  readonly onAssignActive: (form: ReplaceForm) => void;
  /** Active package exists → request an explicit replacement (confirm). */
  readonly onRequestReplace: (form: ReplaceForm, newPackageName: string) => void;
}) {
  const [packageId, setPackageId] = useState<string>(packages[0]?.id ?? '');
  const [startsAt, setStartsAt] = useState<string>('');
  const [endsAt, setEndsAt] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Reconcile the default package when the list changes.
  useEffect(() => {
    if (!packageId || !packages.some((p) => p.id === packageId)) {
      setPackageId(packages[0]?.id ?? '');
    }
  }, [packages, packageId]);

  if (packages.length === 0) {
    return (
      <section className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{hasActive ? 'استبدال الباقة' : 'تعيين باقة'}</h2>
        </div>
        <EmptyState
          title="لا توجد باقات نشطة في الكتالوج"
          description="أنشئ باقة من /app/services قبل تعيينها لعميل."
        />
      </section>
    );
  }

  const selectedPackage = packages.find((p) => p.id === packageId) ?? null;

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!packageId) {
      setError('اختر باقة.');
      return;
    }
    if (startsAt && endsAt && endsAt < startsAt) {
      setError('تاريخ الانتهاء يجب أن يكون بعد تاريخ البدء.');
      return;
    }
    const form: ReplaceForm = {
      packageId,
      startsAt: startsAt || null,
      endsAt: endsAt || null,
      notes: notes.trim() || null,
    };
    if (hasActive) {
      // Explicit replacement — the parent shows a confirm step before
      // ending the current active package.
      onRequestReplace(form, selectedPackage?.name ?? 'باقة');
    } else {
      onAssignActive(form);
    }
  }

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>{hasActive ? 'استبدال الباقة الحالية' : 'تعيين باقة'}</h2>
      </div>
      {hasActive ? (
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 4, marginBottom: 12 }}>
          اختيار باقة مختلفة سيؤدي إلى استبدال الباقة الحالية بعد تأكيد صريح: تُنهى الباقة الحالية
          وتُفعّل الباقة الجديدة (تبقى باقة واحدة فعّالة لكل عميل).
        </p>
      ) : null}
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
            gap: 14,
          }}
        >
          <div className="auth-field" style={{ marginBottom: 0 }}>
            <label className="auth-field-label">الباقة *</label>
            <select
              className="auth-input"
              value={packageId}
              onChange={(e) => setPackageId(e.currentTarget.value)}
              disabled={submitting}
              style={{ paddingInlineStart: 18 }}
            >
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="auth-field" style={{ marginBottom: 0 }}>
            <label className="auth-field-label">تاريخ البدء (اختياري)</label>
            <input
              className="auth-input"
              type="date"
              value={startsAt}
              onChange={(e) => setStartsAt(e.currentTarget.value)}
              disabled={submitting}
              dir="ltr"
              style={{ paddingInlineStart: 18 }}
            />
          </div>
          <div className="auth-field" style={{ marginBottom: 0 }}>
            <label className="auth-field-label">تاريخ الانتهاء (اختياري)</label>
            <input
              className="auth-input"
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.currentTarget.value)}
              disabled={submitting}
              dir="ltr"
              style={{ paddingInlineStart: 18 }}
            />
          </div>
        </div>
        <div className="auth-field" style={{ marginTop: 14, marginBottom: 0 }}>
          <label className="auth-field-label">ملاحظات (اختياري)</label>
          <textarea
            className="auth-input"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            rows={2}
            maxLength={1000}
            disabled={submitting}
            style={{ paddingInlineStart: 18, resize: 'vertical' }}
          />
        </div>

        {selectedPackage ? (
          <div className="tiny" style={{ marginTop: 10, color: 'var(--muted)' }}>
            السعر التلقائي:{' '}
            {selectedPackage.price_amount !== null
              ? `${selectedPackage.price_amount} ${selectedPackage.price_currency}`
              : 'بدون سعر'}{' '}
            · {billingIntervalLabel(selectedPackage.billing_interval)} ·{' '}
            {selectedPackage.services.length} خدمة
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button
            type="submit"
            className="auth-cta"
            disabled={submitting}
            style={{ width: 'auto' }}
          >
            {submitting ? <span className="spinner" /> : null}
            <span>{hasActive ? 'استبدال الباقة' : 'تعيين وتفعيل'}</span>
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}

function statusLabel(s: ClientPackageAssignmentStatus): string {
  switch (s) {
    case 'draft':
      return 'مسودّة';
    case 'active':
      return 'فعّالة';
    case 'paused':
      return 'متوقفة';
    case 'cancelled':
      return 'ملغاة';
    case 'ended':
      return 'منتهية';
    default:
      return s;
  }
}

function statusPill(s: ClientPackageAssignmentStatus): string {
  switch (s) {
    case 'draft':
      return 'draft';
    case 'active':
      return 'active';
    case 'paused':
      return 'paused';
    case 'cancelled':
      return 'error';
    case 'ended':
      return 'archived';
    default:
      return 'info';
  }
}

function billingIntervalLabel(interval: string | null): string {
  switch (interval) {
    case 'monthly':
      return 'شهري';
    case 'quarterly':
      return 'ربع سنوي';
    case 'yearly':
      return 'سنوي';
    case 'custom':
      return 'مخصّص';
    case 'one_time':
      return 'مرّة واحدة';
    default:
      return '—';
  }
}

function handleApiErr(err: unknown, setError: (v: string | null) => void): void {
  if (err instanceof KmiApiError) {
    if (err.code === 'permission_denied') {
      setError('لا تملك صلاحية تعيين الباقات.');
      return;
    }
    if (err.code === 'validation_error') {
      setError('البيانات غير صالحة. تأكد من الحقول وحاول مجدداً.');
      return;
    }
    if (err.code === 'not_found') {
      setError('العميل أو الباقة غير متاحة.');
      return;
    }
  }
  if (isApiError(err)) {
    setError('تعذّر إكمال العملية. حاول مجدداً.');
    return;
  }
  setError('تعذّر إكمال العملية. حاول مجدداً.');
}
